import React from "react";
import {
  Autocomplete,
  Box,
  Grid,
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  Typography,

} from "@mui/material";
import { getOrders, getOrderById } from "../../redux/OrderCRUD";
import { formatOrderOption, FormattedOrderOption } from "@/utils/orderFormatLine";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AddButtonSelect, AddButtonAutocomplete } from "@/__core/hocs";
import {
  LabeledField,
} from "@/__core/components/LabeledField";
import ChannelDialog from "../ChannelDialog";
import WalletDialog from "../WalletDialog";
import CustomerCreationDialog from "@/modules/customers/components/CustomerCreationDialog";
import { useSelector } from 'react-redux';
import { getStatusColor } from '../../utils/statusColor';
import { useOrderStatusContext } from "../../context/OrderStatusContext";
import { getSelectedCompany } from "@/modules/auth/helpers/CompanyStorage";
import LoadingBackdrop from "@/__core/helpers/components/LoadingBackdrop";
import { fetchOrderStatuses } from "../../utils/apiHelpers";
import { TopOrderProps } from "../../models/OrderModel";
import { usePermissions } from "@/__core/permissions";
// Note: Address creation is now handled in NewOrder.tsx when form is submitted

const normalizeOrderStatuses = (statuses: any[]) => {
  const normalized = (Array.isArray(statuses) ? statuses : []).map((status: any) => {
    const categorySource = status.group_status || status.category || null;
    return {
      id: String(status.id || status.value || ''),
      name: String(status.display_name || status.label || status.status_name || status.name || '').trim(),
      color: status.color || undefined,
      is_record_sales: Boolean(status.is_record_sales),
      is_default: Boolean(status.is_default),
      is_active: status.is_active !== false,
      sort_order: status.sort_order ?? 0,
      category: categorySource
        ? {
            id: categorySource.id,
            name: categorySource.name,
            code: categorySource.code || '',
            sort_order: categorySource.sort_order ?? 999,
          }
        : undefined,
    };
  }).filter((status: any) => status.id && status.name && status.is_active);

  return normalized.sort((a: any, b: any) => {
    const groupSortA = a.category?.sort_order ?? 999;
    const groupSortB = b.category?.sort_order ?? 999;
    if (groupSortA !== groupSortB) return groupSortA - groupSortB;

    const itemSortA = a.sort_order ?? 0;
    const itemSortB = b.sort_order ?? 0;
    if (itemSortA !== itemSortB) return itemSortA - itemSortB;

    return (a.name || '').localeCompare(b.name || '', 'vi');
  });
};



export default function TopOrder({
  formData,
  customers,
  salesChannels,
  wallets = [],
  companies,
  orderStatuses,
  contextCompanyId,
  isEditMode = false,
  disabled = false,
  onInputChange,
  onOrderStatusUpdated,
  onSalesChannelCreated,
  onWalletCreated,
}: TopOrderProps) {
  // Try to get refresh function from context, fallback to prop
  let refreshFunction: (() => Promise<void>) | undefined;
  try {
    const context = useOrderStatusContext();
    refreshFunction = context?.refreshOrderStatuses;
  } catch (error) {
    // Context not available, use prop
    refreshFunction = onOrderStatusUpdated;
  }
  
  // Use context function if available, otherwise use prop
  const handleOrderStatusUpdated = refreshFunction || onOrderStatusUpdated;

  const [statusOptions, setStatusOptions] = React.useState(() => normalizeOrderStatuses(orderStatuses));
  const [statusOptionsLoading, setStatusOptionsLoading] = React.useState(false);

  const loadOrderStatuses = React.useCallback(async (force = false) => {
    setStatusOptionsLoading(true);
    try {
      const statuses = await fetchOrderStatuses({ force });
      setStatusOptions(normalizeOrderStatuses(statuses));
    } catch (error) {
      console.error('TopOrder: Error loading order statuses:', error);
      setStatusOptions(normalizeOrderStatuses(orderStatuses));
    } finally {
      setStatusOptionsLoading(false);
    }
  }, [orderStatuses]);

  React.useEffect(() => {
    loadOrderStatuses(false);
  }, [loadOrderStatuses]);

  // Listen for global orderStatusesUpdated event from order status updates.
  React.useEffect(() => {
    const handleOrderStatusesUpdated = async () => {
      if (handleOrderStatusUpdated) {
        try {
          await handleOrderStatusUpdated();
          await loadOrderStatuses(true);
        } catch (error) {
          console.error('TopOrder: Error refreshing order statuses from event:', error);
        }
      }
    };
    
    window.addEventListener('orderStatusesUpdated', handleOrderStatusesUpdated);
    
    return () => {
      window.removeEventListener('orderStatusesUpdated', handleOrderStatusesUpdated);
    };
  }, [handleOrderStatusUpdated, loadOrderStatuses]);
  
  // Orders autocomplete state
  const [orderOptions, setOrderOptions] = React.useState<FormattedOrderOption[]>([]);
  const [orderSearchLoading, setOrderSearchLoading] = React.useState(false);
  const [parentOrderLoading, setParentOrderLoading] = React.useState(false);
  const orderSearchTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch orders on mount
  React.useEffect(() => {
    getOrders().then((res) => {
      const data = res.data?.results || res.data || [];
      setOrderOptions(data.map((o: any) => formatOrderOption(o)));
    }).catch(() => {});
  }, []);

  const handleOrderSearch = React.useCallback((searchText: string) => {
    if (orderSearchTimeout.current) clearTimeout(orderSearchTimeout.current);
    orderSearchTimeout.current = setTimeout(() => {
      setOrderSearchLoading(true);
      getOrders({ search: searchText }).then((res) => {
        const data = res.data?.results || res.data || [];
        setOrderOptions(data.map((o: any) => formatOrderOption(o)));
      }).catch(() => {}).finally(() => setOrderSearchLoading(false));
    }, 300);
  }, []);

  // Get selected company from Redux store
  const selectedCompany = useSelector((state: any) => state.auth.selectedCompany);
  const { hasPermission } = usePermissions();
  const canCreateCustomer = hasPermission('customer', 'create');
  const canCreateWallet = hasPermission('transactionwallet', 'create');
  const canCreateSalesChannel = hasPermission('orders.saleschannel', 'create');

  const orderCodeCompanyPrefix = React.useMemo(() => {
    const normalizeCode = (rawCode?: string | null): string | null => {
      if (!rawCode) return null;
      const code = rawCode.toString().trim().toUpperCase();
      if (!code || code === 'GLOBAL') return null;
      return code;
    };

    const companyFromForm = companies.find((company) => company.id === formData.company_id);
    const formCompanyCode = normalizeCode(companyFromForm?.code);
    const reduxCompanyCode = normalizeCode(selectedCompany?.company_code);
    const storedCompanyCode = normalizeCode(getSelectedCompany()?.company_code);

    return formCompanyCode || reduxCompanyCode || storedCompanyCode || 'XB';
  }, [companies, formData.company_id, selectedCompany]);

  const fixedOrderCodePart = `${orderCodeCompanyPrefix}####-###`;
  const normalizedToolbarCompanyId = React.useMemo(() => {
    const normalized = (contextCompanyId || '').trim();
    if (!normalized || normalized === 'all' || normalized === 'global') return '';
    return normalized;
  }, [contextCompanyId]);

  const normalizedFormCompanyId = React.useMemo(() => {
    const normalized = (formData.company_id || '').trim();
    if (!normalized || normalized === 'all' || normalized === 'global') return '';
    return normalized;
  }, [formData.company_id]);

  const customerDialogCompanyId = normalizedFormCompanyId || normalizedToolbarCompanyId;

  const CustomerCreationCustomDialog = React.useMemo(
    () =>
      function CustomerCreationCustomDialogComponent({
        open,
        onClose,
        onSave,
        loading,
      }: {
        open: boolean;
        onClose: () => void;
        onSave: (data: any) => Promise<any>;
        loading?: boolean;
      }) {
        return (
          <CustomerCreationDialog
            open={open}
            onClose={onClose}
            onSave={onSave}
            loading={loading}
            companyId={customerDialogCompanyId}
          />
        );
      },
    [customerDialogCompanyId]
  );

  const displayedOrderCodePrefix = isEditMode
    ? (formData.order_code_prefix || "")
    : (formData.order_code_prefix || fixedOrderCodePart);
  const displayedOrderCodeSuffix = isEditMode
    ? (formData.order_code_suffix || "")
    : (formData.order_code_suffix ?? formData.order_code ?? "");

  React.useEffect(() => {
    if (!isEditMode && !formData.order_code_prefix) {
      onInputChange("order_code_prefix", fixedOrderCodePart);
    }
  }, [fixedOrderCodePart, formData.order_code_prefix, isEditMode, onInputChange]);

  React.useEffect(() => {
    const shouldSyncFromOrderCode =
      !isEditMode &&
      (formData.order_code_suffix === undefined || formData.order_code_suffix === null || formData.order_code_suffix === "") &&
      !!formData.order_code;

    if (shouldSyncFromOrderCode) {
      onInputChange("order_code_suffix", formData.order_code);
    }
  }, [formData.order_code, formData.order_code_suffix, isEditMode, onInputChange]);
  
  return (
    <Box>
      <LoadingBackdrop
        open={parentOrderLoading}
        message="Đang tải thông tin đơn hàng cha..."
      />
      {/* Order Information Section */}
      <>
        
        <Grid container rowSpacing={0} columnSpacing={4}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label="Mã đơn hàng" labelWidth={120}>
              {isEditMode ? (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                  <TextField
                    value={formData.order_code || ""}
                    size="small"
                    fullWidth
                    disabled={disabled || formData.order_type !== 'free_enter'}
                    onChange={(e) => {
                      const inputCode = e.target.value;
                      onInputChange("order_code", inputCode);
                      if (formData.order_type === 'free_enter') {
                        onInputChange("order_code_prefix", inputCode);
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'text.primary',
                      }
                    }}
                  />
                  {formData.parent_order_info && (
                    <Typography
                      component="span"
                      onClick={() => {
                        if (formData.parent_order_info?.id) {
                          window.dispatchEvent(new CustomEvent('openOrderTab', {
                            detail: {
                              id: formData.parent_order_info.id,
                              code: formData.parent_order_info.code,
                            }
                          }));
                        }
                      }}
                      sx={{
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        color: '#1976d2',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#1565c0',
                        }
                      }}
                      title={`Mở đơn hàng cha: ${formData.parent_order_info.code}`}
                    >
                      {formData.parent_order_info.code}
                    </Typography>
                  )}
                  <Select
                    className="Select__inline-icon"
                    value={formData.order_type || "main"}
                    size="small"
                    disabled
                    sx={{
                      width: 140,
                      flexShrink: 0,
                      ml: 'auto',
                    }}
                  >
                    <MenuItem value="main"><em>Tự tạo</em></MenuItem>
                    <MenuItem value="free_enter"><em>Tự nhập</em></MenuItem>
                    <MenuItem value="ps">Phát sinh</MenuItem>
                    <MenuItem value="bh">Bảo hành</MenuItem>
                  </Select>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {(formData.order_type === 'free_enter') && (
                    <TextField
                      value={displayedOrderCodePrefix}
                      size="small"
                      sx={{ flex: 1, minWidth: 0 }}
                      onChange={(e) => {
                        const inputCode = e.target.value;
                        onInputChange("order_code_prefix", inputCode);
                        onInputChange("order_code", inputCode);
                      }}
                      disabled={disabled}
                      placeholder="Nhập mã đơn hàng"
                    />
                  )}
                  {formData.order_type !== 'free_enter' && formData.order_type !== 'ps' && formData.order_type !== 'bh' && (
                    <TextField
                      value={displayedOrderCodePrefix}
                      size="small"
                      disabled
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        '& .MuiInputBase-input': {
                          color: 'text.primary',
                        }
                      }}
                    />
                  )}
                  {(formData.order_type === 'ps' || formData.order_type === 'bh') && (
                    <Autocomplete
                      freeSolo
                      options={orderOptions}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                      inputValue={formData.order_code_parent || ""}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'input') {
                          onInputChange("order_code_parent", newInputValue);
                          handleOrderSearch(newInputValue);
                        }
                      }}
                      onChange={(_, newValue) => {
                        if (newValue && typeof newValue !== 'string') {
                          onInputChange("order_code_parent", newValue.label);
                          onInputChange("parent_order_id", newValue.id);
                          // Fetch parent order details and auto-fill form
                          if (newValue.id) {
                            setParentOrderLoading(true);
                            getOrderById(newValue.id).then((res) => {
                              const order = res.data;
                              if (order) {
                                if (order.customer_id || order.customer?.id) {
                                  onInputChange("customer_id", order.customer_id || order.customer?.id || "");
                                }
                                if (order.sales_channel_id || order.sales_channel?.id) {
                                  onInputChange("sales_channel_id", order.sales_channel_id || order.sales_channel?.id || "");
                                }
                                if (order.company_id || order.company?.id) {
                                  onInputChange("company_id", order.company_id || order.company?.id || "");
                                }
                                if (order.delivery_phone) {
                                  onInputChange("delivery_phone", order.delivery_phone);
                                }
                                const addrObj = order.delivery_address_obj || order.delivery_address;
                                if (addrObj && typeof addrObj === 'object') {
                                  onInputChange("delivery_address_obj", {
                                    address_line: addrObj.address_line || '',
                                    province: addrObj.province?.id || addrObj.province || '',
                                    ward: addrObj.ward?.id || addrObj.ward || '',
                                    free_address: addrObj.free_address || '',
                                  });
                                }

                              }
                            }).catch(() => {}).finally(() => setParentOrderLoading(false));
                          }
                        } else if (typeof newValue === 'string') {
                          onInputChange("order_code_parent", newValue);
                          onInputChange("parent_order_id", "");
                        } else {
                          onInputChange("order_code_parent", "");
                          onInputChange("parent_order_id", "");
                        }
                      }}
                      loading={orderSearchLoading}
                      size="small"
                      disabled={disabled}
                      sx={{ flex: 1, minWidth: 0 }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Đơn hàng cha"
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', color: option.line1Color || option.statusColor || '#0f172a' }}>
                              {option.displayLabel}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: option.line2Color || '#6b7280' }}>
                              {option.addressLabel}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    />
                  )}
                  <Select
                      className="Select__inline-icon"
                      value={formData.order_type || "main"}
                      size="small"
                      disabled={disabled}
                      onChange={(e) => {
                        const orderType = e.target.value;
                        onInputChange("order_type", orderType);
                      }}
                      sx={{
                        width: 140,
                        flexShrink: 0,
                        ml: 'auto',
                      }}
                  >
                      <MenuItem value="main"><em>Tự tạo</em></MenuItem>
                      <MenuItem value="free_enter"><em>Tự nhập</em></MenuItem>
                      <MenuItem value="ps">Phát sinh</MenuItem>
                      <MenuItem value="bh">Bảo hành</MenuItem>
                  </Select>
                </Box>
              )}
            </LabeledField>
            <LabeledField label="Khách hàng" labelWidth={120}>
              <AddButtonAutocomplete
                options={customers}
                value={customers.find((customer: any) => customer.id === formData.customer_id) || null}
                onChange={(event, newValue) => {
                  onInputChange("customer_id", newValue?.id || "");
                }}
                placeholder="Chọn khách hàng"
                size="small"
                disabled={disabled}
                canAddNew={canCreateCustomer}
                getOptionLabel={(option: any) => {
                  if (!option) return '';
                  const phone = option.phone || option.contact_phone || '';
                  return phone ? `${option.name} - ${phone}` : option.name;
                }}
                isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
                filterOptions={(options, { inputValue }) => {
                  return options.filter((option: any) => {
                    const name = option.name?.toLowerCase() || '';
                    const phone = (option.phone || option.contact_phone || '').toLowerCase();
                    const searchTerm = inputValue.toLowerCase();
                    
                    return name.includes(searchTerm) || phone.includes(searchTerm);
                  });
                }}
                noOptionsText="Không tìm thấy khách hàng"
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
                CustomDialog={CustomerCreationCustomDialog}
                onAddNewCustom={async (customerData: any) => {
                  const customerId = customerData.id || `new-${Date.now()}`;
                  
                  // Update the customer selection in the form when customer is created
                  onInputChange("customer_id", customerId);
                  
                  // Also auto-fill phone if available
                  if (customerData.phone) {
                    onInputChange("delivery_phone", customerData.phone);
                  }
                  
                  // Auto-fill address if available
                  const addressObj = customerData.address_obj_response || customerData.address_obj;
                  if (addressObj && typeof addressObj === 'object') {
                    onInputChange("delivery_address_obj", addressObj);
                  }

                  // Return the new customer data
                  const returnedData = {
                    id: customerId,
                    name: customerData.name,
                    email: customerData.email || `${customerData.name.toLowerCase().replace(/\s+/g, "")}@email.com`,
                    phone: customerData.phone || '',
                    address_obj: customerData.address_obj || null,
                    address_obj_response: customerData.address_obj_response || null,
                  };
                  return returnedData;
                }}
              />
            </LabeledField>
            <LabeledField label="Ngày bán hàng" labelWidth={120}>
                <DatePicker
                  value={formData.sale_date}
                  onChange={(date) => onInputChange("sale_date", date)}
                  format="dd/MM/yyyy"
                  disabled={disabled}
                  slotProps={{
                        textField: {
                          size: "small",
                          variant: "standard",
                          sx: { width: '100%' },
                        },
                      }}
                />
            </LabeledField>
           
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label="Tài khoản thu tiền" labelWidth={120}>
              <AddButtonSelect
                options={wallets.map((wallet) => ({
                  id: wallet.id,
                  name: wallet.name,
                  code: wallet.code,
                }))}
                value={formData.wallet_id || ""}
                onChange={(event: any) => {
                  onInputChange("wallet_id", event.target.value);
                }}
                placeholder="Chọn tài khoản"
                size="small"
                disabled={disabled}
                canAddNew={false}
                CustomDialog={({ open, onClose, onSave, loading }) => (
                  <WalletDialog
                    open={open}
                    onClose={onClose}
                    onSave={onSave}
                    loading={loading}
                  />
                )}
                onAddNewCustom={async (walletData: any) => {
                  const walletId = walletData.id || `new-${Date.now()}`;
                  onInputChange("wallet_id", walletId);

                  const newWallet = {
                    id: walletId,
                    name: walletData.name,
                    code: walletData.code,
                  };

                  if (onWalletCreated) {
                    onWalletCreated(newWallet);
                  }

                  return newWallet;
                }}
              />
            </LabeledField>
            <LabeledField label="Trạng thái" labelWidth={120}>
              <Autocomplete
                options={statusOptions || []}
                value={statusOptions.find((status: any) => status.id === formData.status_id) || null}
                onChange={(event, newValue) => {
                  onInputChange("status_id", newValue?.id || "");
                }}
                size="small"
                disabled={disabled}
                getOptionLabel={(option: any) => {
                  if (!option) return '';
                  return option.name || '';
                }}
                isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                groupBy={(option: any) => option.category?.name || 'Khác'}
                renderInput={(params) => {
                  const selectedStatus = statusOptions.find((status: any) => status.id === formData.status_id);
                  const statusColor = selectedStatus 
                    ? (selectedStatus.color 
                        ? getStatusColor(selectedStatus.id, selectedStatus.color)
                        : getStatusColor(selectedStatus.id))
                    : undefined;
                  
                  return (
                    <TextField
                      {...params}
                      placeholder="Chọn trạng thái"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                        },
                        '& .MuiAutocomplete-clearIndicator': {
                          display: 'none',
                        },
                        '& .MuiAutocomplete-popupIndicator': {
                          display: 'none',
                        },
                        '& .MuiInputBase-input': {
                          color: statusColor || 'inherit',
                          fontWeight: statusColor ? 500 : 'inherit',
                        },
                      }}
                    />
                  );
                }}
                loading={statusOptionsLoading}
                filterOptions={(options, { inputValue }) => {
                  return options.filter((option: any) => {
                    const name = option.name?.toLowerCase() || '';
                    const categoryName = option.category?.name?.toLowerCase() || '';
                    const searchTerm = inputValue.toLowerCase();
                    
                    return name.includes(searchTerm) || categoryName.includes(searchTerm);
                  });
                }}
                renderGroup={(params) => (
                  <li key={params.key} style={{ padding: 0, margin: 0 }}>
                    <ListSubheader
                      disableSticky
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        color: '#4b5563',
                        backgroundColor: '#f9fafb',
                        py: '2px !important',
                        px: '8px',
                        m: '0 !important',
                        minHeight: 'auto !important',
                        lineHeight: '1.2 !important',
                      }}
                    >
                      {params.group}
                    </ListSubheader>
                    {params.children}
                  </li>
                )}
                sx={{
                  '& .MuiAutocomplete-groupUl': {
                    paddingTop: '0 !important',
                    paddingBottom: '0 !important',
                    margin: '0 !important',
                  },
                  '& .MuiAutocomplete-listbox': {
                    paddingTop: '0 !important',
                    paddingBottom: '0 !important',
                  },
                }}
                renderOption={(props, option: any) => {
                  const statusColor = option.color 
                    ? getStatusColor(option.id, option.color)
                    : getStatusColor(option.id);
                  
                  return (
                    <li 
                      {...props} 
                      key={option.id}
                      style={{
                        padding: 0,
                        margin: 0,
                        marginTop: 0,
                        marginBottom: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          color: statusColor,
                          paddingLeft: '24px',
                          py: 0.5,
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                          '&.Mui-focused': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {option.name}
                      </Typography>
                    </li>
                  );
                }}
                noOptionsText="Không tìm thấy trạng thái"
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
                fullWidth
              />
            </LabeledField>
            <LabeledField label="Kênh bán hàng" labelWidth={120}>
              <AddButtonSelect
                options={salesChannels}
                value={formData.sales_channel_id}
                onChange={(event) => onInputChange("sales_channel_id", event.target.value)}
                placeholder="Chọn kênh"
                size="small"
                disabled={disabled}
                canAddNew={false}
                CustomDialog={({ open, onClose, onSave, loading }) => (
                  <ChannelDialog
                    open={open}
                    onClose={onClose}
                    onSave={onSave}
                    loading={loading}
                  />
                )}
                onAddNewCustom={async (channelData: any) => {
                  onInputChange("sales_channel_id", channelData.id);

                  if (onSalesChannelCreated) {
                    onSalesChannelCreated(channelData);
                  }

                  return channelData;
                }}
              />
            </LabeledField>

          </Grid>
        </Grid>
      </>

    </Box>
  );
}
